import { useEffect, useMemo, useState } from "react";
import { createFileRoute, useNavigate, useRouter } from "@tanstack/react-router";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import type { LucideIcon } from "lucide-react";
import {
  ArrowUpRight,
  ClipboardCheck,
  Handshake,
  MessageSquareText,
  PackageCheck,
  PenTool,
  Search,
  Send,
  ShieldCheck,
  SlidersHorizontal,
  Truck,
} from "lucide-react";
import { toast } from "sonner";
import type {
  ServiceRequestListResponse,
  ServiceRequestResponse,
  ServiceRequestUpdateResponse,
} from "@/lib/api/generated";
import { ServiceRequestStatus } from "@/lib/api/generated";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Separator } from "@/components/ui/separator";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { SERVICE_CATALOG, type ServiceCatalogItem } from "@/lib/service-marketplace/catalog";
import {
  createServiceRequestServer,
  listServiceRequestTimelineServer,
  listServiceRequestsServer,
} from "@/server/service-marketplace";
import { cn } from "@/lib/utils";
import { extractApiErrorMessage } from "@/lib/api/error";
import { logError } from "@/lib/structured-log";

interface ServiceVisualConfig {
  Icon: LucideIcon;
  accent: string;
  icon: string;
  iconShell: string;
  cardGlow: string;
}

const SERVICE_VISUALS: Record<ServiceCatalogItem["type"], ServiceVisualConfig> = {
  supplier_verification: {
    Icon: ShieldCheck,
    accent: "from-cyan-300 via-blue-400 to-indigo-400",
    icon: "text-blue-700",
    iconShell: "border-blue-100 bg-blue-50",
    cardGlow: "bg-[radial-gradient(circle_at_18%_0%,rgba(59,130,246,0.12),transparent_32%)]",
  },
  logistics_warehousing: {
    Icon: Truck,
    accent: "from-emerald-300 via-teal-400 to-sky-400",
    icon: "text-teal-700",
    iconShell: "border-teal-100 bg-teal-50",
    cardGlow: "bg-[radial-gradient(circle_at_18%_0%,rgba(20,184,166,0.12),transparent_32%)]",
  },
  product_design: {
    Icon: PenTool,
    accent: "from-violet-300 via-fuchsia-300 to-rose-300",
    icon: "text-fuchsia-700",
    iconShell: "border-fuchsia-100 bg-fuchsia-50",
    cardGlow: "bg-[radial-gradient(circle_at_18%_0%,rgba(217,70,239,0.10),transparent_32%)]",
  },
  quality_inspection: {
    Icon: ClipboardCheck,
    accent: "from-amber-300 via-orange-300 to-red-300",
    icon: "text-orange-700",
    iconShell: "border-orange-100 bg-orange-50",
    cardGlow: "bg-[radial-gradient(circle_at_18%_0%,rgba(249,115,22,0.11),transparent_32%)]",
  },
  procurement_agent: {
    Icon: Handshake,
    accent: "from-lime-300 via-emerald-300 to-cyan-300",
    icon: "text-emerald-700",
    iconShell: "border-emerald-100 bg-emerald-50",
    cardGlow: "bg-[radial-gradient(circle_at_18%_0%,rgba(16,185,129,0.11),transparent_32%)]",
  },
};

function createRequestFormSchema(t: (key: string) => string) {
  return z.object({
    description: z
      .string()
      .trim()
      .min(1, t("pages:serviceMarketplace.form.validation.descriptionRequired"))
      .max(4000, t("pages:serviceMarketplace.form.validation.descriptionMax")),
  });
}

interface RequestFormValues {
  description: string;
}
type ServicesTab = "catalog" | "requests";
type RequestStatusFilter = ServiceRequestStatus | "all";

const REQUEST_STATUS_FILTERS: Array<{ value: RequestStatusFilter; color: string }> = [
  { value: "all", color: "bg-slate-500" },
  { value: ServiceRequestStatus.submitted, color: "bg-blue-500" },
  { value: ServiceRequestStatus.in_review, color: "bg-violet-500" },
  { value: ServiceRequestStatus.in_progress, color: "bg-amber-500" },
  { value: ServiceRequestStatus.waiting_for_customer, color: "bg-orange-500" },
  { value: ServiceRequestStatus.delivered, color: "bg-emerald-500" },
  { value: ServiceRequestStatus.completed, color: "bg-green-500" },
  { value: ServiceRequestStatus.canceled, color: "bg-rose-500" },
];

export const Route = createFileRoute("/workspace/$workspaceSlug/services")({
  validateSearch: (search): { tab?: ServicesTab } => ({
    tab: search.tab === "requests" ? "requests" : "catalog",
  }),
  loader: async ({ context, parentMatchPromise }) => {
    const workspaceId = await getWorkspaceIdWithFallback(context, "services", parentMatchPromise);
    const requests = (await listServiceRequestsServer({
      data: { workspaceId, skip: 0, limit: 50 },
    })) as ServiceRequestListResponse;
    return { workspaceId, requests };
  },
  component: ServicesRoute,
});

function ServicesRoute() {
  const { t, i18n } = useTranslation(["pages", "common"]);
  const { workspaceSlug } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const router = useRouter();
  const { workspaceId, requests } = Route.useLoaderData();
  const [selectedService, setSelectedService] = useState<ServiceCatalogItem | null>(null);
  const [selectedRequest, setSelectedRequest] = useState<ServiceRequestResponse | null>(null);
  const [requestSearch, setRequestSearch] = useState("");
  const [requestStatusFilter, setRequestStatusFilter] = useState<RequestStatusFilter>("all");
  const [timeline, setTimeline] = useState<ServiceRequestUpdateResponse[]>([]);
  const [isTimelineLoading, setIsTimelineLoading] = useState(false);
  const activeTab = search.tab ?? "catalog";
  const requestFormSchema = useMemo(() => createRequestFormSchema(t), [t]);
  const serviceLabels = useMemo(
    () =>
      Object.fromEntries(SERVICE_CATALOG.map((item) => [item.type, t(`pages:${item.titleKey}`)])),
    [t],
  ) as Record<ServiceRequestResponse["service_type"], string>;

  const form = useForm<RequestFormValues>({
    resolver: zodResolver(requestFormSchema),
    defaultValues: { description: "" },
  });

  const sortedRequests = useMemo(
    () =>
      [...(requests.items ?? [])].sort(
        (a, b) => new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime(),
      ),
    [requests.items],
  );
  const filteredRequests = useMemo(() => {
    const query = requestSearch.trim().toLowerCase();
    return sortedRequests.filter((request) => {
      const matchesStatus = requestStatusFilter === "all" || request.status === requestStatusFilter;
      const matchesQuery =
        query.length === 0 ||
        serviceLabels[request.service_type]?.toLowerCase().includes(query) ||
        request.description.toLowerCase().includes(query);
      return matchesStatus && matchesQuery;
    });
  }, [requestSearch, requestStatusFilter, serviceLabels, sortedRequests]);

  useEffect(() => {
    if (!selectedRequest) {
      setTimeline([]);
      return;
    }
    let isActive = true;
    setIsTimelineLoading(true);
    listServiceRequestTimelineServer({
      data: { workspaceId, requestId: selectedRequest.id },
    })
      .then((items) => {
        if (isActive) setTimeline(items as ServiceRequestUpdateResponse[]);
      })
      .catch((error: unknown) => {
        logError("service_marketplace.timeline.load_failed", { error });
        toast.error(t("pages:serviceMarketplace.toasts.timelineLoadFailed"));
      })
      .finally(() => {
        if (isActive) setIsTimelineLoading(false);
      });
    return () => {
      isActive = false;
    };
  }, [selectedRequest, t, workspaceId]);

  async function handleSubmit(values: RequestFormValues): Promise<void> {
    if (!selectedService) return;
    try {
      const created = (await createServiceRequestServer({
        data: {
          workspaceId,
          payload: {
            service_type: selectedService.type,
            description: values.description,
          },
        },
      })) as ServiceRequestResponse;
      toast.success(t("pages:serviceMarketplace.toasts.createSuccess"));
      form.reset();
      setSelectedService(null);
      setSelectedRequest(created);
      await router.invalidate();
      await navigate({
        to: "/workspace/$workspaceSlug/services",
        params: { workspaceSlug },
        search: { tab: "requests" },
      });
    } catch (error) {
      toast.error(extractApiErrorMessage(error, t("pages:serviceMarketplace.toasts.createFailed")));
    }
  }

  return (
    <main className="flex h-full flex-col overflow-hidden bg-white">
      <div className="h-[64px] px-6">
        <div className="flex h-full items-center">
          <h1 className="text-[16px] font-semibold text-gray-900">
            {t("pages:serviceMarketplace.title")}
          </h1>
        </div>
      </div>
      <Separator className="bg-[#dfdfdf]" />

      <Tabs
        value={activeTab}
        onValueChange={(tab) =>
          navigate({
            to: "/workspace/$workspaceSlug/services",
            params: { workspaceSlug },
            search: { tab: tab as ServicesTab },
          })
        }
        className="flex min-h-0 flex-1 flex-col gap-0 overflow-hidden"
      >
        <div className="shrink-0">
          <TabsList className="flex h-[58px] w-full items-end justify-start gap-8 rounded-none border-b border-gray-200 bg-transparent px-6 py-0 text-gray-500">
            <TabsTrigger
              value="catalog"
              className="h-full flex-none cursor-pointer gap-2 rounded-none border-x-0 border-t-0 border-b border-transparent bg-transparent px-0 text-[14px] font-normal text-gray-500 shadow-none hover:text-gray-900 data-[state=active]:border-x-0 data-[state=active]:border-t-0 data-[state=active]:border-b-gray-950 data-[state=active]:bg-transparent data-[state=active]:text-gray-950"
            >
              <PackageCheck className="size-4 shrink-0" />
              {t("pages:serviceMarketplace.tabs.catalog")}
            </TabsTrigger>
            <TabsTrigger
              value="requests"
              className="h-full flex-none cursor-pointer gap-2 rounded-none border-x-0 border-t-0 border-b border-transparent bg-transparent px-0 text-[14px] font-normal text-gray-500 shadow-none hover:text-gray-900 data-[state=active]:border-x-0 data-[state=active]:border-t-0 data-[state=active]:border-b-gray-950 data-[state=active]:bg-transparent data-[state=active]:text-gray-950"
            >
              <MessageSquareText className="size-4 shrink-0" />
              {t("pages:serviceMarketplace.tabs.requests")}
            </TabsTrigger>
          </TabsList>
        </div>

        <div className="min-h-0 flex-1 overflow-auto px-6 py-6">
          <TabsContent value="catalog" className="m-0">
            <section className="grid grid-cols-[repeat(auto-fill,minmax(280px,340px))] gap-4">
              {SERVICE_CATALOG.map((service) => {
                const visual = SERVICE_VISUALS[service.type];
                const ServiceIcon = visual.Icon;

                return (
                  <article
                    key={service.type}
                    className={cn(
                      "group relative flex min-h-[268px] flex-col overflow-hidden rounded-xl border border-slate-200/80 bg-white p-5 shadow-[0_1px_2px_rgba(15,23,42,0.05),0_12px_30px_rgba(15,23,42,0.04)] transition-all duration-200 hover:-translate-y-0.5 hover:border-slate-300 hover:shadow-[0_4px_12px_rgba(15,23,42,0.08),0_18px_42px_rgba(15,23,42,0.08)]",
                      visual.cardGlow,
                    )}
                  >
                    <div
                      className={cn("absolute inset-x-0 top-0 h-1 bg-gradient-to-r", visual.accent)}
                    />
                    <div className="relative flex items-start justify-between gap-3">
                      <span
                        className={cn(
                          "flex size-8 items-center justify-center rounded-lg border shadow-[0_1px_1px_rgba(15,23,42,0.04)]",
                          visual.iconShell,
                        )}
                      >
                        <ServiceIcon className={cn("size-4", visual.icon)} />
                      </span>
                      <Badge
                        variant="outline"
                        className="h-7 rounded-full border-slate-200 bg-white/80 px-3 text-[12px] font-medium text-slate-700 shadow-[0_1px_2px_rgba(15,23,42,0.04)] backdrop-blur"
                      >
                        {t("pages:serviceMarketplace.catalog.serviceBadge")}
                      </Badge>
                    </div>
                    <h2 className="relative mt-5 text-[17px] font-semibold leading-6 text-slate-950">
                      {t(`pages:${service.titleKey}`)}
                    </h2>
                    <p className="relative mt-2 text-[14px] leading-6 text-slate-600">
                      {t(`pages:${service.scenarioKey}`)}
                    </p>
                    <div className="relative mt-4 mb-5 flex flex-col items-start gap-2">
                      {service.deliverableKeys.map((item) => (
                        <span
                          key={item}
                          className="rounded-full border border-slate-200 bg-white/75 px-3 py-1 text-[12px] leading-5 text-slate-500 shadow-[0_1px_1px_rgba(15,23,42,0.03)]"
                        >
                          {t(`pages:${item}`)}
                        </span>
                      ))}
                    </div>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="relative mt-auto h-10 rounded-full border-slate-300 bg-white text-sm font-semibold text-slate-950 shadow-[0_1px_2px_rgba(15,23,42,0.08),0_6px_18px_rgba(15,23,42,0.06)] hover:border-slate-950 hover:bg-slate-950 hover:text-white"
                      onClick={() => setSelectedService(service)}
                    >
                      <Send className="size-4" />
                      {t(`pages:${service.ctaKey}`)}
                    </Button>
                  </article>
                );
              })}
            </section>
          </TabsContent>

          <TabsContent value="requests" className="m-0">
            <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="flex h-10 min-w-[260px] max-w-[520px] flex-1 items-center gap-3 rounded-full border border-transparent bg-muted/60 px-3 transition-colors focus-within:border-blue-500">
                <Search className="size-4 shrink-0 text-muted-foreground" />
                <div className="min-w-0 flex-1">
                  <Input
                    value={requestSearch}
                    onChange={(event) => setRequestSearch(event.target.value)}
                    placeholder={t(
                      "pages:serviceMarketplace.requests.searchPlaceholder",
                      "Search requests",
                    )}
                    className="h-8 min-w-0 border-0 bg-transparent px-0 text-sm shadow-none placeholder:text-muted-foreground focus-visible:ring-0"
                  />
                </div>
                <Tooltip>
                  <DropdownMenu>
                    <TooltipTrigger asChild>
                      <DropdownMenuTrigger asChild>
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="size-8 shrink-0 rounded-full bg-muted-foreground/10 text-foreground hover:bg-muted-foreground/15"
                          aria-label={t("pages:common.tooltips.showSearchOptions")}
                        >
                          <SlidersHorizontal className="size-4" />
                        </Button>
                      </DropdownMenuTrigger>
                    </TooltipTrigger>
                    <DropdownMenuContent
                      align="end"
                      sideOffset={10}
                      className="w-60 rounded-xl border-slate-200 p-2 shadow-[0_10px_28px_rgba(15,23,42,0.16)]"
                    >
                      {REQUEST_STATUS_FILTERS.map((option) => {
                        const isSelected = requestStatusFilter === option.value;
                        const label =
                          option.value === "all"
                            ? t("pages:serviceMarketplace.requests.allStatuses", "All statuses")
                            : t(`pages:serviceMarketplace.status.${option.value}`);

                        return (
                          <DropdownMenuItem
                            key={option.value}
                            className="flex h-12 cursor-pointer items-center gap-3 rounded-lg px-3 text-[15px] text-slate-950 focus:bg-slate-100"
                            onSelect={() => setRequestStatusFilter(option.value)}
                          >
                            <span className="flex size-5 shrink-0 items-center justify-center">
                              {isSelected ? (
                                <span className="h-2.5 w-4 rotate-[-45deg] border-b-2 border-l-2 border-slate-950" />
                              ) : null}
                            </span>
                            <span className={cn("size-2.5 shrink-0 rounded-full", option.color)} />
                            <span className="min-w-0 truncate">{label}</span>
                          </DropdownMenuItem>
                        );
                      })}
                    </DropdownMenuContent>
                  </DropdownMenu>
                  <TooltipContent side="bottom" align="end">
                    {t("pages:common.tooltips.showSearchOptions")}
                  </TooltipContent>
                </Tooltip>
              </div>
            </div>
            <section className="overflow-hidden rounded-[16px] border border-border/60 bg-card">
              <div className="grid grid-cols-[minmax(0,1.3fr)_160px_180px_120px] gap-4 border-b border-[#dfdfdf] px-4 py-3 text-sm font-medium text-gray-700">
                <div>{t("pages:serviceMarketplace.requests.table.service")}</div>
                <div>{t("pages:serviceMarketplace.requests.table.status")}</div>
                <div>{t("pages:serviceMarketplace.requests.table.updatedAt")}</div>
                <div className="text-right">
                  {t("pages:serviceMarketplace.requests.table.actions")}
                </div>
              </div>
              {filteredRequests.length === 0 ? (
                <div className="flex h-[220px] items-center justify-center px-4 py-12 text-center text-sm text-muted-foreground">
                  {t("pages:serviceMarketplace.requests.empty")}
                </div>
              ) : (
                filteredRequests.map((request) => (
                  <div
                    key={request.id}
                    className="grid grid-cols-[minmax(0,1.3fr)_160px_180px_120px] items-center gap-4 border-b border-gray-100 px-4 py-4 text-sm last:border-b-0"
                  >
                    <div>
                      <div className="font-medium text-gray-950">
                        {serviceLabels[request.service_type]}
                      </div>
                      <div className="mt-1 line-clamp-1 text-gray-500">{request.description}</div>
                    </div>
                    <div>
                      <Badge
                        variant={
                          request.status === ServiceRequestStatus.completed
                            ? "default"
                            : "secondary"
                        }
                      >
                        {t(`pages:serviceMarketplace.status.${request.status}`)}
                      </Badge>
                    </div>
                    <div className="text-gray-600">
                      {formatDateTime(request.updated_at, i18n.language)}
                    </div>
                    <div className="text-right">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => setSelectedRequest(request)}
                      >
                        {t("pages:serviceMarketplace.actions.view")}
                        <ArrowUpRight className="size-4" />
                      </Button>
                    </div>
                  </div>
                ))
              )}
            </section>
          </TabsContent>
        </div>
      </Tabs>

      <Sheet
        open={selectedService !== null}
        onOpenChange={(open) => !open && setSelectedService(null)}
      >
        <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-xl">
          <SheetHeader className="border-b px-6 py-5">
            <SheetTitle>{selectedService ? t(`pages:${selectedService.titleKey}`) : ""}</SheetTitle>
            <SheetDescription>{t("pages:serviceMarketplace.form.description")}</SheetDescription>
          </SheetHeader>
          <Form {...form}>
            <form className="space-y-4 px-6 py-5" onSubmit={form.handleSubmit(handleSubmit)}>
              <FormField
                control={form.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>{t("pages:serviceMarketplace.form.descriptionLabel")}</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        rows={7}
                        placeholder={t("pages:serviceMarketplace.form.descriptionPlaceholder")}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={() => setSelectedService(null)}>
                  {t("common:buttons.cancel")}
                </Button>
                <Button type="submit" disabled={form.formState.isSubmitting}>
                  {form.formState.isSubmitting
                    ? t("pages:serviceMarketplace.actions.submitting")
                    : t("pages:serviceMarketplace.actions.submit")}
                </Button>
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <Sheet
        open={selectedRequest !== null}
        onOpenChange={(open) => !open && setSelectedRequest(null)}
      >
        <SheetContent className="w-full overflow-y-auto p-0 sm:max-w-2xl">
          <SheetHeader className="border-b px-6 py-5">
            <SheetTitle>
              {selectedRequest
                ? serviceLabels[selectedRequest.service_type]
                : t("pages:serviceMarketplace.requests.detailTitle")}
            </SheetTitle>
            <SheetDescription>
              {t("pages:serviceMarketplace.requests.detailDescription")}
            </SheetDescription>
          </SheetHeader>
          {selectedRequest ? (
            <div className="space-y-5 px-6 py-5">
              <div className="flex flex-wrap items-center gap-2">
                <Badge>{t(`pages:serviceMarketplace.status.${selectedRequest.status}`)}</Badge>
                <span className="text-sm text-gray-500">
                  {t("pages:serviceMarketplace.requests.submittedAt", {
                    value: formatDateTime(selectedRequest.submitted_at, i18n.language),
                  })}
                </span>
              </div>
              <div className="rounded-lg bg-gray-50 p-4 text-sm leading-6 text-gray-700">
                {selectedRequest.description}
              </div>
              <Separator />
              <div className="space-y-3">
                <div className="flex items-center gap-2 text-sm font-medium text-gray-950">
                  <MessageSquareText className="size-4" />
                  {t("pages:serviceMarketplace.timeline.title")}
                </div>
                {isTimelineLoading ? (
                  <div className="text-sm text-gray-500">{t("common:loading")}</div>
                ) : timeline.length === 0 ? (
                  <div className="text-sm text-gray-500">
                    {t("pages:serviceMarketplace.timeline.empty")}
                  </div>
                ) : (
                  <div className="space-y-3">
                    {timeline.map((item) => (
                      <div key={item.id} className="rounded-lg border border-gray-200 p-4">
                        <div className="flex flex-wrap items-center justify-between gap-2">
                          <div className="font-medium text-gray-950">
                            {item.title ||
                              t(`pages:serviceMarketplace.updateTypes.${item.update_type}`)}
                          </div>
                          <span className="text-xs text-gray-500">
                            {formatDateTime(item.created_at, i18n.language)}
                          </span>
                        </div>
                        {item.content_text ? (
                          <p className="mt-2 whitespace-pre-wrap text-sm leading-6 text-gray-700">
                            {item.content_text}
                          </p>
                        ) : null}
                        {item.assets && item.assets.length > 0 ? (
                          <div className="mt-3 flex flex-wrap gap-2">
                            {item.assets.map((asset) => (
                              <a
                                key={asset.id}
                                href={asset.url || undefined}
                                target="_blank"
                                rel="noreferrer"
                                className="rounded-md border border-gray-200 px-2.5 py-1 text-xs text-gray-700 hover:bg-gray-50"
                              >
                                {asset.display_name || asset.filename}
                              </a>
                            ))}
                          </div>
                        ) : null}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </main>
  );
}

function formatDateTime(value: string, language: string): string {
  return new Intl.DateTimeFormat(language, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  }).format(new Date(value));
}
